import { useEffect, useState } from 'react';
import { Container, Table, Badge, Spinner, Form, Modal, Button } from 'react-bootstrap';
import Wrapper from '../../components/Wrapper';
import { issueApi } from '../../services/issueApi';

const STATUS_OPTIONS = ['Open', 'In Progress', 'Resolved'];

const statusVariant = (s) => {
  if (s === 'Resolved') return 'success';
  if (s === 'In Progress') return 'warning';
  return 'secondary';
};

const IssueManagement = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  // Solution modal state
  const [modal, setModal] = useState({ show: false, issue_id: null, text: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchIssues = async () => {
    setLoading(true);
    try {
      const res = await issueApi.getAllIssues();
      setIssues(Array.isArray(res.data) ? res.data : []);
    } catch {
      setIssues([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchIssues(); }, []);

  const handleStatusChange = async (issue_id, newStatus) => {
    if (newStatus === 'Resolved') {
      setModal({ show: true, issue_id, text: '' });
      return;
    }
    setUpdating(issue_id);
    try {
      await issueApi.updateIssueStatus(issue_id, newStatus);
      setIssues((prev) =>
        prev.map((i) =>
          i.issue_id === issue_id
            ? { ...i, status: newStatus, updated_at: new Date().toISOString() }
            : i
        )
      );
    } finally {
      setUpdating(null);
    }
  };

  const handleSolutionSubmit = async () => {
    if (!modal.text.trim()) return;
    setSubmitting(true);
    try {
      await issueApi.addSolution(modal.issue_id, modal.text);
      setIssues((prev) =>
        prev.map((i) =>
          i.issue_id === modal.issue_id
            ? { ...i, status: 'Resolved', solution: modal.text, updated_at: new Date().toISOString() }
            : i
        )
      );
      setModal({ show: false, issue_id: null, text: '' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Wrapper>
      <Container fluid className="mt-4">
        <h5 className="mb-4 fw-bold">Issue Management</h5>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <Table responsive bordered hover>
            <thead className="table-dark">
              <tr>
                <th>#</th>
                <th>Field Officer</th>
                <th>Mobile</th>
                <th>Partner</th>
                <th>Issue</th>
                <th>Attachment</th>
                <th>Solution</th>
                <th>Status</th>
                <th>Raised At</th>
                <th>Last Updated</th>
                <th>Update Status</th>
              </tr>
            </thead>
            <tbody>
              {issues.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center text-muted py-4">
                    No issues raised yet.
                  </td>
                </tr>
              ) : (
                issues.map((item) => (
                  <tr key={item.issue_id}>
                    <td>{item.issue_id}</td>
                    <td>{item.field_officer_name}</td>
                    <td>{item.mobile_no}</td>
                    <td>{item.partner_name || '—'}</td>
                    <td style={{ maxWidth: 250 }}>{item.issue}</td>
                    
                    <td>
                      {item.image_url ? (
                        <a 
                          href={item.image_url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-primary text-decoration-none fw-medium"
                        >
                          View Photo
                        </a>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>

                    <td style={{ maxWidth: 250 }} className="text-muted">
                      {item.solution || '—'}
                    </td>
                    <td>
                      <Badge bg={statusVariant(item.status)}>{item.status}</Badge>
                    </td>
                    <td>{new Date(item.raised_at).toLocaleDateString()}</td>
                    <td>{new Date(item.updated_at).toLocaleDateString()}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <Form.Select
                          size="sm"
                          value={item.status}
                          onChange={(e) => handleStatusChange(item.issue_id, e.target.value)}
                          disabled={updating === item.issue_id || item.status === 'Resolved'}
                          style={{ width: 'auto' }}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s}>{s}</option>
                          ))}
                        </Form.Select>
                        {updating === item.issue_id && (
                          <Spinner size="sm" animation="border" />
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </Container>

      {/* Solution Modal */}
      <Modal show={modal.show} onHide={() => setModal({ show: false, issue_id: null, text: '' })} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 fw-bold">Provide Solution</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label className="fw-semibold">Solution</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Describe how the issue was resolved..."
              value={modal.text}
              onChange={(e) => setModal((prev) => ({ ...prev, text: e.target.value }))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setModal({ show: false, issue_id: null, text: '' })}
          >
            Cancel
          </Button>
          <Button
            variant="dark"
            size="sm"
            disabled={submitting || !modal.text.trim()}
            onClick={handleSolutionSubmit}
          >
            {submitting ? <Spinner size="sm" animation="border" /> : 'Mark as Resolved'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Wrapper>
  );
};

export default IssueManagement;